import { IconName } from '@fortawesome/fontawesome-svg-core';
import { BMEvent } from 'components/eventView/pages/eventData/BMEvent';
import { Bater2024Event } from 'components/eventView/pages/eventData/Bater2024Event';
import { BenatskaEvent } from 'components/eventView/pages/eventData/BenatskaEvent';
import { ByglandEvent } from 'components/eventView/pages/eventData/ByglandEvent';
import { CyclingEvent } from 'components/eventView/pages/eventData/CyclingEvent';
import { DstEvent } from 'components/eventView/pages/eventData/DstEvent';
import { FjellsportEvent } from 'components/eventView/pages/eventData/FjellsportEvent';
import { FredrikstadEvent } from 'components/eventView/pages/eventData/FredrikstadEvent';
import { ItaliaTourBolognaEvent } from 'components/eventView/pages/eventData/ItaliaTourBolognaEvent';
import { KarieraPlus2024Event } from 'components/eventView/pages/eventData/KarieraPlus2024Event';
import { KDag2024Event } from 'components/eventView/pages/eventData/Kdag2024Event';
import { KoieneEvent } from 'components/eventView/pages/eventData/KoieneEvent';
import { KonopexEvent } from 'components/eventView/pages/eventData/KonopexEvent';
import { Liege2024Event } from 'components/eventView/pages/eventData/Liege2024Event';
import { MarenaEvent } from 'components/eventView/pages/eventData/MarenaEvent';
import { MarenaShowcaseEvent } from 'components/eventView/pages/eventData/MarenaShowcase';
import { NamsskoganEvent } from 'components/eventView/pages/eventData/NamsskoganEvent';
import { NlsDays2023Event } from 'components/eventView/pages/eventData/NlsDays2023Event';
import { NlsDays2024Event } from 'components/eventView/pages/eventData/NlsDays2024Event';
import { NlsDays2025Event } from 'components/eventView/pages/eventData/NlsDays2025Event';
import { Seafood2024Event } from 'components/eventView/pages/eventData/Seafood2024Event';
import { UtrechtEvent } from 'components/eventView/pages/eventData/UtrechtEvent';
import { ZaKarierou2024Event } from 'components/eventView/pages/eventData/ZaKarierou2024Event';

import {
    DetailQuery_moment_cards,
    DetailQuery_moment_header
} from 'graphql/query/__generated__/DetailQuery';

export interface EventEntityCategory {
    categoryId: string;
    orderIndex: number;
    title: string;
}

export interface EventProgramItem {
    programId: string;
    title?: string;
    description?: string;
    startDateTime: string;
    endDateTime?: string;
    placeName?: string;
    categoryIds?: string[];
    markerId?: string;
    storyId?: string;
    storyIds?: string[];
    storyIdsToOpen?: string[];
    autoStoryId?: boolean;
}

export interface EventEntityItem {
    title: string;
    categoryIds: string[];
    imageUrl: string;
    isHighlighted: boolean;
    storyId: string;
    markerId: string;
    storyHeader: DetailQuery_moment_header;
    storyCards: DetailQuery_moment_cards[];
}

export interface EventSpeakerItem {
    storyId: string;
    storyHeader: DetailQuery_moment_header;
    storyCards: DetailQuery_moment_cards[];
}

export type ButtonKeys =
    | 'home'
    | 'entities'
    | 'speakers'
    | 'map'
    | 'program'
    | 'information'
    | string;

export interface EventEntityMultifilter {
    filterId: string;
    title: string;
    optionIds: EventEntityCategory[];
}

export interface EventEntityDefinition {
    id: string;
    name: string;
    description?: string;
    icon: IconName;
    list: EventEntityItem[];
    link?: {
        text: string;
        url: string;
    };
    isMapEntity: boolean;
    searchable: boolean;
    categories: EventEntityCategory[];
    multifilters: EventEntityMultifilter[];
}

type HomepageContentSlots =
    | 'footer'
    | 'custom-block-buttons-1'
    | 'custom-block-buttons-2'
    | 'bater-pc-header';

export interface EventInfo {
    noNumbers?: boolean;
    order?: {
        mobile?: ButtonKeys[];
        wide?: (ButtonKeys | HomepageContentSlots)[];
        primes?: ButtonKeys[];
        normal?: ButtonKeys[];
        normalV2?: (ButtonKeys | HomepageContentSlots)[][];
    };
    homepageContent?: Partial<Record<HomepageContentSlots, () => React.ReactNode>>;
    newsletter?: {
        formId: string;
        title: string;
        description: string;
        thankYouText: string;
        backgroundColor: string;
        buttonBgColor: string;
        color: string;
    };
    externalLinkPosition?: 'top' | 'bottom';
    externalLinkStyle?: 'large' | 'small';
    externalLinkButtonBackground?: string;
    externalLinkButtonTextColor?: string;
    language?: string;
    name?: string;
    brandImageUrl?: string;
    headerImageUrl?: string; //Usually same as the brand
    homepageTopImageUrl?: string;
    logoUrl?: string;
    logoBgColor?: string;
    mapPreviewUrl?: string;
    hasMap?: boolean;
    description?: string;
    startDateTime?: string;
    endDateTime?: string;
    otherEntities?: Array<{
        id: string;
        name: string;
        icon: IconName;
        listIds: string[];
        description?: string;
        link?: {
            text: string;
            url: string;
        };
        searchable?: boolean;
        categoryIds?: string[];
        customOrderIds?: string[]; //TODO for custom entities
        isMapEntity?: boolean;
    }>;
    externalLinks?: {
        title: string;
        url: string;
    }[];
    program?: {
        items: EventProgramItem[];
        dateExtensionHours: number;
    };
    information?: {
        listIds: string[];
    };

    styling?: {
        circle?: {
            bgColor?: string;
            iconColor?: string;
        };
        matchColor?: string;
        tileColor?: string;
        pageHeaderShadow?: string;
    };
}

interface FilterSettingsSimple {
    type: 'simple';
    optionIds: string[];
}

interface FilterSettingsComplex {
    type: 'complex';
    filters: {
        filterId: string;
        title: string;
        optionIds: string[];
    }[];
}

type FilterSettings = FilterSettingsSimple | FilterSettingsComplex;

interface ListViewSettings {
    viewType: 'default' | 'sections';
}
export interface EventViewData {
    eventInfo?: EventInfo;
    eventId: string;
    guideId?: string;
    mapId: string;
    mapWrapper?: string;
    mapSettings?: {
        standZoom?: number;
    };
    filterSettings?: Record<string, FilterSettings>;
    listViewSettings?: Record<string, ListViewSettings>;
    programSettings?: {
        showAddToCalendar?: boolean;
        programTitle?: string;
        timezone?: string;
        searchable?: boolean;
        // Sets bi-directional mapping between speaker list and program
        speakersListId?: string;
        embedItemBackgroundColor?: string;
    };
}

export interface informationListItem {
    storyId: string;
    title: string;
    description: string;
    storyCards: DetailQuery_moment_cards[];
}

export const EmptyEventData: EventViewData = {
    eventId: '',
    mapId: '',
    eventInfo: {
        order: {
            mobile: [],
            wide: [],
            primes: ['entities'],
            normal: []
        },
        externalLinkPosition: 'top',
        externalLinkStyle: 'large',
        hasMap: true,
        name: '',
        language: 'en',
        brandImageUrl: '',
        headerImageUrl: '',
        logoUrl: '',
        mapPreviewUrl: '',
        description: '',
        program: {
            items: [],
            dateExtensionHours: 0
        },
        information: {
            listIds: []
        },
        styling: {
            circle: {
                bgColor: '#3A3A3A',
                iconColor: '#ffffff'
            },
            matchColor: '#000000',
            tileColor: '#ffffff'
        }
    },
    programSettings: {
        showAddToCalendar: false,
        timezone: ''
    }
};

export const EventData: Record<string, EventViewData> = {
    bm: BMEvent,
    // hoyma: HoymaEvent,
    // uia: UIAEvent,
    // scandic: ScandicHotelEvent,
    fredrikstad: FredrikstadEvent,
    fjellsport: FjellsportEvent,
    marena: MarenaEvent,
    marenaa: MarenaShowcaseEvent,
    karieraplus2024: KarieraPlus2024Event,
    namsskogan: NamsskoganEvent,
    koiene: KoieneEvent,
    'utrecht-careers-day-2024': UtrechtEvent,
    benatska: BenatskaEvent,
    seafood: Seafood2024Event,
    kdag: KDag2024Event,
    cycling: CyclingEvent,
    nlsdays2023: NlsDays2023Event,
    nlsdays2024: NlsDays2024Event,
    nlsdays2025: NlsDays2025Event,
    'liege-career-night': Liege2024Event,
    'italia-bologna': ItaliaTourBolognaEvent,
    zakarierou: ZaKarierou2024Event,
    konopex: KonopexEvent,
    bater2024: Bater2024Event,
    dst: DstEvent,
    bygland: ByglandEvent
};
